import { useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Phone, Mail, MapPin, Clock, Instagram, MessageCircle, Send } from "lucide-react";
import { Reveal, PageHero, Eyebrow } from "@/components/Motion";
import { CONTACTS, IMAGES } from "@/data/content";
import { useLanguage } from "@/i18n";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const inputClass =
  "w-full border border-stone-300 bg-[#FFFFFF] px-4 py-3.5 text-sm text-stone-900 placeholder:text-stone-500 transition-colors duration-300 focus:border-[#8F6B3C]";

export default function Contatti() {
  const { t, content } = useLanguage();
  const [form, setForm] = useState({
    nome: "",
    email: "",
    telefono: "",
    servizio: "",
    messaggio: "",
    privacy: false,
  });
  const [sending, setSending] = useState(false);

  const update = (key) => (e) =>
    setForm((f) => ({ ...f, [key]: e.target.type === "checkbox" ? e.target.checked : e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSending(true);

    try {
      await axios.post(`${API}/leads`, {
        nome: form.nome,
        email: form.email || null,
        telefono: form.telefono || null,
        servizio: form.servizio || null,
        messaggio: form.messaggio,
      });
    } catch (err) {
      console.error(err);
    }

    const text = encodeURIComponent(
      `${t("waHeader")}\n\n` +
        `${t("waName")}: ${form.nome}\n` +
        (form.email ? `Email: ${form.email}\n` : "") +
        (form.telefono ? `${t("waPhone")}: ${form.telefono}\n` : "") +
        (form.servizio ? `${t("waService")}: ${form.servizio}\n` : "") +
        `\n${t("waMessage")}:\n${form.messaggio}`
    );
    window.open(`https://wa.me/393208908036?text=${text}`, "_blank");
    toast.success(t("toastSent"));
    setSending(false);
    setForm({ nome: "", email: "", telefono: "", servizio: "", messaggio: "", privacy: false });
  };

  return (
    <main data-testid="contatti-page">
      <PageHero
        eyebrow={t("contactsHeroEyebrow")}
        lines={[t("contactsL1"), t("contactsL2")]}
        sub={t("contactsSub")}
        img={IMAGES.contatti}
      />

      <section className="mx-auto max-w-7xl px-6 pb-24 md:pb-32">
        <div className="grid gap-14 lg:grid-cols-12 lg:gap-16">
          {/* RECAPITI */}
          <Reveal className="lg:col-span-5">
            <Eyebrow>{t("recapiti")}</Eyebrow>
            <h2 className="mt-4 font-serif text-3xl uppercase leading-tight text-stone-900 md:text-4xl">
              {t("reachableA")} <span className="italic text-[#8F6B3C]">{t("reachableB")}</span>
            </h2>

            <ul className="mt-10 space-y-7">
              <li>
                <a data-testid="contact-phone-link" href={CONTACTS.phoneHref} className="group flex items-start gap-4">
                  <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full border border-stone-300 text-[#8F6B3C] transition-colors duration-300 group-hover:border-[#C89D66]">
                    <Phone className="h-4 w-4" />
                  </span>
                  <span>
                    <span className="block font-mono text-[10px] uppercase tracking-[0.25em] text-stone-500">{t("labelPhone")}</span>
                    <span className="mt-1 block text-lg text-stone-900 transition-colors group-hover:text-[#8F6B3C]">
                      {CONTACTS.phone}
                    </span>
                  </span>
                </a>
              </li>
              <li>
                <a data-testid="contact-email-link" href={`mailto:${CONTACTS.email}`} className="group flex items-start gap-4">
                  <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full border border-stone-300 text-[#8F6B3C] transition-colors duration-300 group-hover:border-[#C89D66]">
                    <Mail className="h-4 w-4" />
                  </span>
                  <span>
                    <span className="block font-mono text-[10px] uppercase tracking-[0.25em] text-stone-500">{t("labelEmail")}</span>
                    <span className="mt-1 block text-lg text-stone-900 transition-colors group-hover:text-[#8F6B3C]">
                      {CONTACTS.email}
                    </span>
                  </span>
                </a>
              </li>
              <li>
                <a data-testid="contact-address-link" href={CONTACTS.mapsLink} target="_blank" rel="noopener noreferrer" className="group flex items-start gap-4">
                  <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full border border-stone-300 text-[#8F6B3C] transition-colors duration-300 group-hover:border-[#C89D66]">
                    <MapPin className="h-4 w-4" />
                  </span>
                  <span>
                    <span className="block font-mono text-[10px] uppercase tracking-[0.25em] text-stone-500">{t("labelAddress")}</span>
                    <span className="mt-1 block text-lg leading-snug text-stone-900 transition-colors group-hover:text-[#8F6B3C]">
                      {CONTACTS.address}
                    </span>
                  </span>
                </a>
              </li>
              <li className="flex items-start gap-4">
                <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full border border-stone-300 text-[#8F6B3C]">
                  <Clock className="h-4 w-4" />
                </span>
                <span>
                  <span className="block font-mono text-[10px] uppercase tracking-[0.25em] text-stone-500">{t("labelHours")}</span>
                  <span className="mt-1 block text-lg text-stone-900">{CONTACTS.hours}</span>
                </span>
              </li>
            </ul>

            <div className="mt-10 flex flex-wrap gap-3">
              <a
                data-testid="contact-whatsapp-direct"
                href={CONTACTS.whatsapp}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 whitespace-nowrap bg-[#C89D66] px-6 py-3.5 font-mono text-[11px] uppercase tracking-[0.15em] text-[#0F1012] transition-colors duration-300 hover:bg-[#D4A972]"
              >
                <MessageCircle className="h-4 w-4" />
                {t("chatDirect")}
              </a>
              <a
                data-testid="contact-instagram-link"
                href={CONTACTS.instagram}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 border border-stone-300 px-6 py-3.5 font-mono text-[11px] uppercase tracking-[0.2em] text-stone-800 transition-colors duration-300 hover:border-[#C89D66] hover:text-[#8F6B3C]"
              >
                <Instagram className="h-4 w-4" />
                Instagram
              </a>
            </div>
          </Reveal>

          {/* FORM WHATSAPP */}
          <Reveal delay={0.15} className="lg:col-span-7">
            <div className="border border-stone-300 bg-[#FFFFFF] p-8 md:p-12">
              <Eyebrow>{t("formEyebrow")}</Eyebrow>
              <h2 className="mt-4 font-serif text-2xl uppercase leading-tight text-stone-900 md:text-3xl">
                {t("formTitleA")} <span className="italic text-[#8F6B3C]">{t("formTitleB")}</span>
              </h2>
              <p className="mt-3 text-sm leading-relaxed text-stone-500">
                {t("formSub")}
              </p>

              <form data-testid="contact-form" onSubmit={handleSubmit} className="mt-8 space-y-5">
                <div className="grid gap-5 sm:grid-cols-2">
                  <div>
                    <label htmlFor="nome" className="mb-2 block font-mono text-[10px] uppercase tracking-[0.25em] text-stone-500">
                      {t("labelName")}
                    </label>
                    <input
                      id="nome"
                      data-testid="contact-nome-input"
                      required
                      value={form.nome}
                      onChange={update("nome")}
                      placeholder={t("placeholderName")}
                      className={inputClass}
                    />
                  </div>
                  <div>
                    <label htmlFor="telefono" className="mb-2 block font-mono text-[10px] uppercase tracking-[0.25em] text-stone-500">
                      {t("labelPhoneField")}
                    </label>
                    <input
                      id="telefono"
                      data-testid="contact-telefono-input"
                      type="tel"
                      value={form.telefono}
                      onChange={update("telefono")}
                      placeholder="+39 ..."
                      className={inputClass}
                    />
                  </div>
                </div>
                <div className="grid gap-5 sm:grid-cols-2">
                  <div>
                    <label htmlFor="email" className="mb-2 block font-mono text-[10px] uppercase tracking-[0.25em] text-stone-500">
                      {t("labelEmail")}
                    </label>
                    <input
                      id="email"
                      data-testid="contact-email-input"
                      type="email"
                      value={form.email}
                      onChange={update("email")}
                      placeholder="mario.rossi@email.it"
                      className={inputClass}
                    />
                  </div>
                  <div>
                    <label htmlFor="servizio" className="mb-2 block font-mono text-[10px] uppercase tracking-[0.25em] text-stone-500">
                      {t("labelService")}
                    </label>
                    <select
                      id="servizio"
                      data-testid="contact-servizio-select"
                      value={form.servizio}
                      onChange={update("servizio")}
                      className={`${inputClass} appearance-none`}
                    >
                      <option value="">{t("selectService")}</option>
                      {content.services.map((s) => (
                        <option key={s.id} value={s.title}>
                          {s.title}
                        </option>
                      ))}
                      <option value="Altro">{t("other")}</option>
                    </select>
                  </div>
                </div>
                <div>
                  <label htmlFor="messaggio" className="mb-2 block font-mono text-[10px] uppercase tracking-[0.25em] text-stone-500">
                    {t("labelMessage")}
                  </label>
                  <textarea
                    id="messaggio"
                    data-testid="contact-messaggio-input"
                    required
                    rows={5}
                    value={form.messaggio}
                    onChange={update("messaggio")}
                    placeholder={t("placeholderMsg")}
                    className={`${inputClass} resize-none`}
                  />
                </div>
                <label className="flex cursor-pointer items-start gap-3 text-xs leading-relaxed text-stone-500">
                  <input
                    data-testid="contact-privacy-checkbox"
                    type="checkbox"
                    required
                    checked={form.privacy}
                    onChange={update("privacy")}
                    className="mt-0.5 h-4 w-4 shrink-0 accent-[#C89D66]"
                  />
                  {t("privacyA")}{" "}
                  <Link
                    to="/privacy"
                    data-testid="contact-privacy-link"
                    className="text-[#8F6B3C] underline underline-offset-2 hover:text-[#7A5A30]"
                  >
                    {t("privacyLink")}
                  </Link>
                  .
                </label>
                <button
                  data-testid="contact-submit-btn"
                  type="submit"
                  disabled={sending}
                  className="group inline-flex w-full items-center justify-center gap-3 whitespace-nowrap bg-[#C89D66] px-8 py-4 font-mono text-[11px] uppercase tracking-[0.15em] text-[#0F1012] transition-colors duration-300 hover:bg-[#D4A972] disabled:opacity-60 sm:w-auto"
                >
                  <Send className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
                  {sending ? t("sending") : t("submitBtn")}
                </button>
              </form>
            </div>
          </Reveal>
        </div>

        {/* MAPPA */}
        <Reveal className="mt-20">
          <div data-testid="contact-map" className="overflow-hidden border border-stone-300">
            <iframe
              title="Mappa Edilcomplex2020 — Capannori (LU)"
              src={CONTACTS.googleMaps}
              className="map-dark h-[420px] w-full border-0"
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
            />
          </div>
        </Reveal>
      </section>
    </main>
  );
}
